import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Coins, ArrowLeft, Loader2, CreditCard, CalendarClock, ExternalLink, PiggyBank } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { toast } from "sonner";

const money = (cents) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;
const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return null; }
};
const PACK_LABEL = { credits_small: "100 credits", credits_medium: "500 credits", credits_large: "1500 credits" };
const PLAN_LABEL = { pro_monthly: "Pro · Monthly", pro_yearly: "Pro · Annual", studio_monthly: "Studio · Monthly", studio_yearly: "Studio · Annual" };
const txnLabel = (k) => PLAN_LABEL[k] || PACK_LABEL[k] || k;

const PLAN_ACCENT = {
  free: { ring: "border-white/15", text: "text-white/70", btn: "bg-white/10 hover:bg-white/20 text-white" },
  pro: { ring: "border-[#00E5FF]/50", text: "text-[#00E5FF]", btn: "bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black" },
  studio: { ring: "border-[#FF007F]/50", text: "text-[#FF007F]", btn: "bg-[#FF007F] hover:bg-[#FF007F]/90 text-white" },
};

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const [billing, setBilling] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setData(data)).catch(() => {});
    api.get("/payments/history").then(({ data }) => setBilling(data)).catch(() => {});
  }, []);

  const checkout = async (lookup_key) => {
    if (!lookup_key) return;
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Billing portal isn't available yet.");
      setPortalLoading(false);
    }
  };

  const owner = !!(user && user.is_owner);
  const currentPlan = owner ? "owner" : ((billing?.plan) || (user && user.plan) || "free");
  const credits = billing?.credits ?? user?.credits ?? 0;
  const renewal = fmtDate(billing?.plan_expires);
  const payments = billing?.payments || [];
  const isPaid = !owner && currentPlan !== "free";

  return (
    <div className="min-h-screen bg-background text-white">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/50 hover:text-white text-sm mb-6" data-testid="pricing-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Billing / account panel */}
        <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5 mb-10" data-testid="billing-panel">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-6">
              <div data-testid="billing-current-plan">
                <div className="text-white/40 text-xs uppercase tracking-wide">Current plan</div>
                <div className="font-display text-lg font-semibold capitalize flex items-center gap-2">
                  {currentPlan}
                  {owner && <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#FFD54A]/50 text-[#FFD54A] uppercase">Internal · Unlimited</span>}
                  {isPaid && <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/20 text-white/50 uppercase">Active</span>}
                </div>
              </div>
              <div data-testid="billing-credits">
                <div className="text-white/40 text-xs uppercase tracking-wide">Credits</div>
                <div className="font-display text-lg font-semibold flex items-center gap-1.5"><Coins className="w-4 h-4 text-[#39FF14]" />{owner ? "Unlimited" : credits}</div>
              </div>
              {isPaid && renewal && (
                <div data-testid="billing-renewal">
                  <div className="text-white/40 text-xs uppercase tracking-wide flex items-center gap-1"><CalendarClock className="w-3 h-3" />Renews / expires</div>
                  <div className="font-display text-lg font-semibold">{renewal}</div>
                </div>
              )}
            </div>
            {isPaid && (
              <Button variant="outline" onClick={openPortal} disabled={portalLoading}
                className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white text-sm h-9" data-testid="manage-billing-btn">
                {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}Manage billing
              </Button>
            )}
          </div>

          {payments.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/10" data-testid="billing-history">
              <div className="text-white/40 text-xs uppercase tracking-wide mb-2 flex items-center gap-1"><CreditCard className="w-3 h-3" />Payment history</div>
              <div className="space-y-1.5">
                {payments.map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-sm" data-testid={`payment-row-${i}`}>
                    <span className="text-white/70">{txnLabel(t.lookup_key)}</span>
                    <span className="flex items-center gap-3">
                      <span className="text-white/40">{fmtDate(t.created_at)}</span>
                      <span className="font-medium text-white">{t.amount_total != null ? money(t.amount_total) : "—"}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="text-center mb-10">
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Level up your studio</h1>
          <p className="text-white/50 mt-3 text-base">Subscribe for higher limits, HD exports and premium AI — top up with credits anytime.</p>
          <div className="inline-flex items-center gap-2 mt-6 rounded-full border border-white/15 p-1" data-testid="billing-toggle">
            <button onClick={() => setAnnual(false)} data-testid="billing-monthly"
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${!annual ? "bg-white text-black" : "text-white/60"}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} data-testid="billing-annual"
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${annual ? "bg-white text-black" : "text-white/60"}`}>Annual <span className="text-[#39FF14] text-xs">·2 mo free</span></button>
          </div>
        </div>

        {!data ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" /></div>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-6" data-testid="plans-grid">
              {data.plans.map((p) => {
                const acc = PLAN_ACCENT[p.key] || PLAN_ACCENT.free;
                const price = annual ? p.yearly : p.monthly;
                const lookup = annual ? p.lookup_yearly : p.lookup_monthly;
                const isCurrent = currentPlan === p.key;
                const yearlySaving = p.monthly > 0 && p.yearly > 0 ? p.monthly * 12 - p.yearly : 0;
                return (
                  <div key={p.key} className={`relative rounded-2xl border ${acc.ring} bg-zinc-950/60 p-6 flex flex-col ${p.key === "pro" ? "md:-mt-3 md:mb-3 shadow-[0_0_40px_-12px_#00E5FF55]" : ""}`} data-testid={`plan-card-${p.key}`}>
                    {p.key === "pro" && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[#00E5FF] text-black text-[11px] font-bold uppercase tracking-wide">Most popular</span>}
                    <div className="flex items-center gap-2">
                      <span className={`font-display text-xl font-semibold ${acc.text}`}>{p.name}</span>
                      {isCurrent && <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/20 text-white/50 uppercase">Current</span>}
                    </div>
                    <div className="mt-4 flex items-end gap-1">
                      <span className="text-4xl font-bold">{money(price)}</span>
                      <span className="text-white/40 text-sm mb-1">/{annual ? "yr" : "mo"}</span>
                    </div>
                    {annual && yearlySaving > 0 && (
                      <div className="mt-2 inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14] text-xs font-semibold" data-testid={`annual-savings-${p.key}`}>
                        <PiggyBank className="w-3.5 h-3.5" />Save {money(yearlySaving)}/yr
                      </div>
                    )}
                    <ul className="mt-6 space-y-2.5 flex-1">
                      {p.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-white/70"><Check className={`w-4 h-4 mt-0.5 shrink-0 ${acc.text}`} />{f}</li>
                      ))}
                    </ul>
                    <Button disabled={p.key === "free" || isCurrent || loadingKey === lookup} onClick={() => checkout(lookup)}
                      className={`mt-6 h-11 rounded-full font-medium ${acc.btn} disabled:opacity-40`} data-testid={`plan-cta-${p.key}`}>
                      {loadingKey === lookup ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting…</> :
                        p.key === "free" ? "Included" : isCurrent ? "Your plan" : <><Sparkles className="w-4 h-4 mr-2" />Choose {p.name}</>}
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="mt-14">
              <h2 className="font-display text-lg text-center mb-1">Need more credits?</h2>
              <p className="text-white/40 text-sm text-center mb-6">One-time top-ups for heavy rendering & AI usage.</p>
              <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto" data-testid="credit-packs-grid">
                {data.credit_packs.map((c) => (
                  <div key={c.lookup_key} className="rounded-xl border border-white/10 bg-zinc-950/60 p-5 text-center" data-testid={`credit-pack-${c.lookup_key}`}>
                    <Coins className="w-6 h-6 text-[#39FF14] mx-auto" />
                    <div className="mt-2 font-semibold text-lg">{c.credits} credits</div>
                    <div className="text-white/40 text-sm">{money(c.amount)}</div>
                    <Button variant="outline" disabled={loadingKey === c.lookup_key} onClick={() => checkout(c.lookup_key)}
                      className="mt-4 w-full h-9 rounded-full border-[#39FF14]/40 bg-transparent hover:bg-[#39FF14]/10 text-[#39FF14] text-sm" data-testid={`credit-cta-${c.lookup_key}`}>
                      {loadingKey === c.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
